import { Box, Card, CardContent, LinearProgress, makeStyles, Typography } from '@material-ui/core';
import { LinkedPatient, PatientProgress, ProgramPhase } from 'hydra-data-types';
import moment from 'moment';
import { FunctionComponent } from 'react';
import nervaLogo from '../../../assets/svgs/logos/nerva-logo-with-name.svg';
import { isValidNumber } from '../../../helpers/NumberUtils';
import { colors } from '../../../theme/colors';
import { Compliance } from './Compliance';

type PatientCardProps = { linkedPatient: LinkedPatient };
export const PatientCard: FunctionComponent<PatientCardProps> = (props) => {
  const {
    linkedPatient: { name, startTime },
  } = props;
  const progress = props.linkedPatient.progress ?? undefined;
  const missedDays = progress?.missedDays ?? undefined;

  const classes = useStyles();
  const programNotStarted =
    typeof progress === 'object' &&
    linkedNotStarted({
      phase: progress?.phase,
      day: progress?.day,
      hasAccess: progress?.hasAccess,
      startTime,
    });

  return (
    <Card elevation={1} className={classes.patientCard}>
      <CardContent>
        <Typography variant="body1" noWrap>
          <b>{name}</b>
        </Typography>
        {typeof startTime === 'number' && (
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {<StartDate startTime={startTime} programNotStarted={programNotStarted} />}
            {typeof progress === 'object' && (
              <Compliance programNotStarted={programNotStarted} missedDays={missedDays} />
            )}
          </Box>
        )}
        <Card elevation={0} className={classes.programProgressCard}>
          <CardContent className={classes.programProgressContent}>
            <Box display="flex" alignItems="flex-start">
              <Box display="flex" alignItems="flex-start">
                <img src={nervaLogo} alt="nerva logo" width="80" height="35" />
              </Box>

              <Box width="100%" paddingLeft={3}>
                <ProgressLabel progress={progress} programNotStarted={programNotStarted} />
                <LinearProgress
                  variant="determinate"
                  className={classes.progressBar}
                  value={progressPercentage(progress)}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};

type StartDateProps = { startTime: number; programNotStarted: boolean };
const StartDate: FunctionComponent<StartDateProps> = (props) => (
  <Box marginTop={1}>
    <Typography variant="body2" noWrap>
      <b>
        {props.programNotStarted
          ? 'N/A'
          : `Started ${moment(props.startTime).local().format('ddd DD MMM, YYYY')}`}
      </b>
    </Typography>
  </Box>
);

type ProgressLabelProps = Pick<LinkedPatient, 'progress'> & {
  programNotStarted: boolean;
};
const ProgressLabel: FunctionComponent<ProgressLabelProps> = (props) => {
  const { progress, programNotStarted } = props;

  return typeof progress === 'object' && !programNotStarted ? (
    <Typography variant="caption">
      {progress.phase === ProgramPhase.Maintenance
        ? 'Finished'
        : `Week ${Math.floor(progress.day / 7) + 1}, Day ${(progress.day % 7) + 1}`}
    </Typography>
  ) : (
    <Typography variant="caption" noWrap>
      Not started
    </Typography>
  );
};

function progressPercentage(progress: PatientProgress | undefined): number {
  let percentage = 0;

  if (typeof progress === 'object') {
    const { phase, completeRatio } = progress;
    if (phase === ProgramPhase.Maintenance) {
      percentage = 100;
    } else {
      percentage = isValidNumber(completeRatio) ? completeRatio * 100 : 0;
    }
  }

  return percentage;
}

type LinkedNotStartedParams = Pick<PatientProgress, 'day' | 'phase' | 'hasAccess'> &
  Pick<LinkedPatient, 'startTime'>;
function linkedNotStarted(params: LinkedNotStartedParams): boolean {
  const { day, phase, hasAccess, startTime } = params;

  if ((day === 0 && phase === ProgramPhase.Core && !hasAccess) || !isValidNumber(startTime)) {
    return true;
  }
  return false;
}

const useStyles = makeStyles({
  patientCard: {
    padding: 4,
    borderRadius: 20,
    height: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  programProgressCard: {
    marginTop: 14,
    height: '41px',
    borderRadius: 14,
    backgroundColor: colors.xxLightGray,
  },
  programProgressContent: {
    paddingTop: 3,
    paddingBottom: 12,
    '&:last-child': {
      paddingBottom: 10,
    },
  },
  progressBar: {
    backgroundColor: '#D2D2D2',
    borderRadius: 100,
    height: 3,
  },
});
